import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const SLIDESHOW_IMAGES = [
  { url: '/images/slide01.jpg', alt: 'Party Crowd having fun' },
  { url: '/images/slide02.jpg', alt: 'Wedding couple in photobooth' },
  { url: '/images/slide03.jpg', alt: 'Funny props moment' },
  { url: '/images/slide04.jpg', alt: 'Group photo laughter' },
];

export const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDESHOW_IMAGES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="hero" className="relative h-screen overflow-hidden" data-testid="hero-section">
      {/* Slideshow Background */}
      <div className="absolute inset-0">
        {SLIDESHOW_IMAGES.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image.url}
              alt={image.alt}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
        {/* Light Gradient Overlay - weniger hell für bessere Bilddarstellung */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-white/40 to-white" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 pt-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="text-gray-800 font-bold text-sm md:text-base tracking-widest uppercase mb-4 block bg-white/70 px-4 py-2 rounded-full inline-block">
            FotoBox-Verleih am Bodensee
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-4xl sm:text-5xl md:text-7xl font-extrabold tracking-tight text-gray-900 mb-6 drop-shadow-sm"
          
          data-testid="hero-headline"
        >
          Mach dein Event<br />
          <span className="text-[#2BB8AF] drop-shadow-sm">Unvergesslich</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-gray-700 text-base md:text-lg max-w-xl mb-10 bg-white/70 px-6 py-3 rounded-xl"
        >
          Die Fotobox für unvergessliche Momente in Singen, Radolfzell, Konstanz & am gesamten Bodensee.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-primary inline-flex items-center justify-center cursor-pointer"
            data-testid="hero-cta-primary"
          >
            Jetzt Anfragen
          </button>
          <button
            onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-secondary inline-flex items-center justify-center cursor-pointer"
            data-testid="hero-cta-secondary"
          >
            Preise ansehen
          </button>
        </motion.div>

        {/* Slide Indicators */}
        <div className="absolute bottom-32 flex gap-2" data-testid="slide-indicators">
          {SLIDESHOW_IMAGES.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentSlide 
                  ? 'bg-[#3ECDC3] w-8' 
                  : 'bg-gray-400/50 hover:bg-gray-500'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Scroll Indicator */}
        <motion.button
          onClick={() => document.getElementById('events')?.scrollIntoView({ behavior: 'smooth' })}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="absolute bottom-10 text-gray-400 hover:text-[#3ECDC3] transition-colors cursor-pointer"
          aria-label="Scroll down"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <ChevronDown size={32} />
          </motion.div>
        </motion.button>
      </div>
    </section>
  );
};
